import React, { useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowDownTrayIcon, DocumentTextIcon, CheckCircleIcon, BoltIcon, ExclamationTriangleIcon, ArrowPathIcon, ShieldCheckIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2pdf from 'html2pdf.js';

const BCPSectionSummary = ({ title, data }) => {
  // Skip empty sections
  if (!data || Object.keys(data).length === 0) return null;
  
  // Extract key values from the section data
  const keyItems = Object.entries(data)
    .filter(([_, value]) => value && value !== '' && (typeof value !== 'object' || Object.keys(value).length > 0))
    .slice(0, 3); // Only show first 3 items
  
  if (keyItems.length === 0) return null;
  
  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <div className="bg-gray-50 rounded-md p-3">
        <ul className="text-sm space-y-1">
          {keyItems.map(([key, value]) => (
            <li key={key} className="text-gray-700">
              {typeof value === 'string' ? (
                value.length > 60 ? `${value.substring(0, 60)}...` : value
              ) : (
                Array.isArray(value) ? `${value.length} items` : `${Object.keys(value).length} properties`
              )}
            </li>
          ))}
          {Object.keys(data).length > 3 && (
            <li className="text-gray-500 italic">+ {Object.keys(data).length - 3} more items</li>
          )}
        </ul>
      </div>
    </div>
  );
};

const InsightCard = ({ title, content, icon, bgColor, iconBgColor, textColor }) => {
  const Icon = icon;
  
  return (
    <div className={`${bgColor} overflow-hidden shadow rounded-lg`}>
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center">
          <div className={`flex-shrink-0 ${iconBgColor} rounded-md p-3`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <h3 className={`text-sm font-medium ${textColor}`}>{title}</h3>
            <p className={`mt-2 text-sm ${textColor} opacity-80`}>
              {content || "No insight available"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BCPSummaryPage = ({ formData, previewContent, onRevise, aiInsights }) => {
  const pdfRef = useRef(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  // Debug logging for AI insights
  console.log("BCPSummaryPage received aiInsights:", aiInsights);
  
  // Extract sections with data
  const completedSections = Object.entries(formData || {})
    .filter(([_, sectionData]) => sectionData && Object.keys(sectionData).length > 0);
  
  // Calculate completion metrics
  const totalSections = 17; // This should match the total number of sections in the schema
  const completedSectionCount = completedSections.length;
  
  // Ensure completion percentage doesn't exceed 100%
  const completionPercentage = Math.min(100, Math.round((completedSectionCount / totalSections) * 100));
  
  const handleDownloadPDF = () => {
    const element = pdfRef.current;
    if (!element) {
      console.error('PDF content element not found');
      return;
    }

    try {
      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: 'Business_Continuity_Plan.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };
      
      // Show loading state
      const loadingNotification = document.createElement('div');
      loadingNotification.className = 'fixed bottom-4 right-4 bg-blue-600 text-white py-2 px-4 rounded-md shadow';
      loadingNotification.textContent = 'Generating PDF...';
      document.body.appendChild(loadingNotification);
      
      html2pdf().set(opt).from(element).save().then(() => {
        // Remove loading state on success
        document.body.removeChild(loadingNotification);
      }).catch(error => {
        console.error('PDF generation failed:', error);
        document.body.removeChild(loadingNotification);
        
        // Show error notification
        const errorNotification = document.createElement('div');
        errorNotification.className = 'fixed bottom-4 right-4 bg-red-600 text-white py-2 px-4 rounded-md shadow';
        errorNotification.textContent = 'PDF generation failed. Please try again.';
        document.body.appendChild(errorNotification);
        
        // Remove error notification after 3 seconds
        setTimeout(() => {
          document.body.removeChild(errorNotification);
        }, 3000);
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Could not generate PDF. Please try again later.');
    }
  };
  
  return (
    <div className="flex h-screen overflow-hidden bg-succession-bg-secondary transition-theme">
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-succession-text-primary transition-theme">Business Continuity Plan Summary</h1>
                </div>
                <button
                  type="button"
                  onClick={onRevise}
                  className="inline-flex items-center px-4 py-2 border border-succession-border text-sm font-medium rounded-md shadow-sm text-succession-text-secondary bg-succession-bg-primary hover:bg-succession-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary transition-theme"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Editor
                </button>
              </div>
              
              {/* Prominent Completion Banner */}
              <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-5 sm:px-8 sm:py-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between">
                    <div className="mb-4 sm:mb-0 text-center sm:text-left">
                      <div className="flex items-center mb-2">
                        <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                        <h2 className="text-xl font-bold text-green-800">Congratulations!</h2>
                      </div>
                      <p className="text-lg font-medium text-green-700">Your Business Continuity Plan is complete.</p>
                      <p className="mt-1 text-sm text-green-600">
                        You've successfully created a comprehensive plan to protect your business.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={onRevise}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <DocumentTextIcon className="h-5 w-5 mr-2" />
                        Revise BCP
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPreviewModal(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <EyeIcon className="h-5 w-5 mr-2" />
                        Preview BCP
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadPDF}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                        Download PDF
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-succession-bg-primary shadow-succession-md overflow-hidden sm:rounded-lg mb-8 border border-succession-border transition-theme">
                <div className="px-4 py-5 sm:px-6 bg-succession-bg-tertiary transition-theme">
                  <h2 className="text-lg font-medium text-succession-text-primary transition-theme">Plan Overview</h2>
                  <p className="mt-1 max-w-2xl text-sm text-succession-text-secondary transition-theme">
                    {completionPercentage < 100 && 
                      `Your Business Continuity Plan is ${completionPercentage}% complete (${completedSectionCount} of ${totalSections} sections).`
                    }
                  </p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  {/* AI Insights Section */}
                  {aiInsights && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                        <BoltIcon className="h-5 w-5 text-indigo-500 mr-2" />
                        AI-Generated Insights
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InsightCard 
                          title="Key Strengths"
                          content={aiInsights.key_strengths}
                          icon={CheckCircleIcon}
                          bgColor="bg-green-50"
                          iconBgColor="bg-green-500"
                          textColor="text-green-800"
                        />
                        
                        <InsightCard 
                          title="Identified Risks"
                          content={aiInsights.identified_risks}
                          icon={ExclamationTriangleIcon}
                          bgColor="bg-yellow-50"
                          iconBgColor="bg-yellow-500"
                          textColor="text-yellow-800"
                        />
                        
                        <InsightCard 
                          title="Action Recommendations"
                          content={aiInsights.action_recommendations}
                          icon={ArrowPathIcon}
                          bgColor="bg-blue-50"
                          iconBgColor="bg-blue-500"
                          textColor="text-blue-800"
                        />
                        
                        <InsightCard 
                          title="Compliance Status"
                          content={aiInsights.compliance_status}
                          icon={ShieldCheckIcon}
                          bgColor="bg-purple-50"
                          iconBgColor="bg-purple-500"
                          textColor="text-purple-800"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Preview Modal */}
              {showPreviewModal && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                  <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    {/* Background overlay */}
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowPreviewModal(false)}></div>
                    
                    {/* Modal panel */}
                    <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                      <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                            Business Continuity Plan Preview
                          </h3>
                          <button
                            type="button"
                            className="text-gray-400 hover:text-gray-500"
                            onClick={() => setShowPreviewModal(false)}
                          >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-6 w-6" />
                          </button>
                        </div>
                        <div className="bg-gray-50 rounded-md p-4 max-h-[70vh] overflow-y-auto">
                          <div className="prose prose-sm prose-indigo max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {previewContent || '# Business Continuity Plan\n\nNo preview available.'}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                          type="button"
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={() => setShowPreviewModal(false)}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Hidden div for PDF generation */}
              <div className="hidden">
                <div ref={pdfRef} className="p-8 bg-white" style={{ width: '8.5in', maxWidth: '8.5in', fontFamily: 'Arial, sans-serif' }}>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h1 className="text-3xl font-bold mb-2">Business Continuity Plan</h1>
                    <p className="text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
                  </div>
                  
                  <hr className="my-4 border-t border-gray-300" />
                  
                  {/* AI Insights for PDF */}
                  {aiInsights && (
                    <div style={{ marginBottom: '20px' }}>
                      <h2 style={{ fontSize: '18pt', marginBottom: '10px' }}>AI-Generated Insights</h2>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold text-succession-success mb-2">Key Strengths</h3>
                        <p className="text-sm text-succession-success">{aiInsights.key_strengths}</p>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold text-succession-warning mb-2">Identified Risks</h3>
                        <p className="text-sm text-succession-warning">{aiInsights.identified_risks}</p>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold text-succession-primary mb-2">Action Recommendations</h3>
                        <p className="text-sm text-succession-primary">{aiInsights.action_recommendations}</p>
                      </div>
                      
                      <div style={{ marginBottom: '15px' }}>
                        <h3 className="text-base font-semibold text-succession-secondary mb-2">Compliance Status</h3>
                        <p className="text-sm text-succession-secondary">{aiInsights.compliance_status}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="prose max-w-none" style={{ fontSize: '12pt', lineHeight: '1.5' }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {previewContent || '# Business Continuity Plan\n\nNo content available.'}
                    </ReactMarkdown>
                  </div>
                  
                  <div className="mt-8 text-xs text-succession-text-muted text-center">
                    <p>This document was automatically generated from the Business Continuity Plan tool.</p>
                    <p>© {new Date().getFullYear()} FindBob. All rights reserved.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BCPSummaryPage; 